import React, { useEffect, useState } from 'react';

// SERVICES
import {
  listFieldsEstablishment,
  listFieldsPromotion,
  fieldTranslations,
  actions,
  actionTranslations,
} from './lists';

// LIBS
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

// STYLES
import {
  Autocomplete,
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { InvisbleButton } from 'js/components/Clubs/Segmentation/segmentStyles';

// ASSETS
import DeleteIcon from '@mui/icons-material/Delete';

// COMPONENTS
import AutocompleteListType from './AutocompleteListType';
import ChipArrayContains from './ChipArrayContains';

export default function Query({
  groups,
  setGroups,
  form,
  selectedCardType,
  infosForEdit,
  loadingList,
  setLoadingList,
  selectedEstablishment,
  setSelectedEstablishment,
  selectedStore,
  setSelectedStore,
  selectedProduct,
  setSelectedProduct,
  selectedIdEdit,
  setSelectedIdEdit,
  invalidFields,
  setInvalidFields,
}) {
  const [conditions, setConditions] = useState({});
  const conditionsList = ['E', 'OU'];

  // Media query do material ui
  const isMdUp = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down(750));
  const isMdDown = useMediaQuery((theme) => theme.breakpoints.down(1030));

  useEffect(() => {
    if (selectedCardType.action !== infosForEdit.action) {
      setGroups({});
      setSelectedIdEdit('');
    }
  }, [selectedCardType.action, infosForEdit.action]);

  const isFieldAlreadySelected = (field) => {
    return Object.values(groups).some((group) => group.some((item) => item.field === field));
  };

  // Função para obter as opções baseadas no tipo de ação
  const getOptions = (action) => {
    const fieldsList =
      action === 'establishment_list' ? listFieldsEstablishment : listFieldsPromotion;

    return fieldsList.map((field) => ({
      value: field,
      fieldName: fieldTranslations[field] || field,
      disabled: (field === 'establishmentId' || field === 'id') && isFieldAlreadySelected(field),
      key: field,
    }));
  };

  const getFieldTranslation = (field) => fieldTranslations[field] || field;

  const handleSelectFields = (event, newValue) => {
    if (newValue !== null && !newValue.disabled) {
      const groupKeys = Object.keys(groups);
      const lastGroupKey = groupKeys.length > 0 ? groupKeys[groupKeys.length - 1] : 'grupo1';

      setGroups((prevGroups) => ({
        ...prevGroups,
        [lastGroupKey]: [
          ...(prevGroups[lastGroupKey] || []),
          {
            field: newValue.value,
            type: actions[0],
            value: '',
          },
        ],
      }));
    }
  };

  const handleChangeActions = (event, groupId, itemIndex, fieldValue) => {
    const newAction = event.target.value;
    const currentField = groups[groupId][itemIndex].field;

    // Verifica se a ação é permitida para o campo
    const allowedActions = getFilteredActions(currentField);
    if (!allowedActions.includes(newAction)) {
      return; // Não atualiza se a ação não for permitida
    }

    setGroups((prevGroups) => {
      const updatedGroup = [...prevGroups[groupId]];
      updatedGroup[itemIndex] = {
        ...updatedGroup[itemIndex],
        type: newAction,
        value: fieldValue,
      };

      return {
        ...prevGroups,
        [groupId]: updatedGroup,
      };
    });
  };

  const getFilteredActions = (field) => {
    // Campos que são IDs (só permitem comparações exatas)
    if (['establishmentId', 'id'].includes(field)) {
      return actions.filter((action) =>
        ['==', '!=', 'array_contains', 'array_not_contains'].includes(action)
      );
    }

    // Campos numéricos (permitem comparações matemáticas)
    if (['cashbackDefault', 'segmentIds'].includes(field)) {
      return actions.filter((action) => !['like', 'includes'].includes(action));
    }

    // Campos de texto (permitem busca textual)
    if (['nome', 'establishmentName', 'description', 'detailsDescription'].includes(field)) {
      return actions.filter((action) => !['<', '>'].includes(action));
    }

    // Para todos os outros campos, retorna todas as ações
    return actions;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    setConditions('E');

    const sourceGroupId = result.source.droppableId;
    const destinationGroupId = result.destination.droppableId;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // Movimento entre grupos diferentes
    if (sourceGroupId !== destinationGroupId) {
      setGroups((prevGroups) => {
        const sourceGroup = [...prevGroups[sourceGroupId]];
        const destinationGroup = prevGroups[destinationGroupId]
          ? [...prevGroups[destinationGroupId]]
          : [];

        const [itemToMove] = sourceGroup.splice(sourceIndex, 1);
        destinationGroup.splice(destinationIndex, 0, itemToMove);

        const updatedGroups = {
          ...prevGroups,
          [sourceGroupId]: sourceGroup,
          [destinationGroupId]: destinationGroup,
        };

        // Remove grupos vazios
        Object.keys(updatedGroups).forEach((key) => {
          if (updatedGroups[key].length === 0) {
            delete updatedGroups[key];
          }
        });

        return updatedGroups;
      });
    } else {
      // Movimento dentro do mesmo grupo
      setGroups((prevGroups) => {
        const group = [...prevGroups[sourceGroupId]];
        const [removed] = group.splice(sourceIndex, 1);
        group.splice(destinationIndex, 0, removed);

        return {
          ...prevGroups,
          [sourceGroupId]: group,
        };
      });
    }
  };

  const handleDeleteDrag = (e, groupId, itemIndex) => {
    e.preventDefault();
    setGroups((prevGroups) => {
      const updatedGroup = [...prevGroups[groupId]];
      updatedGroup.splice(itemIndex, 1);

      const updatedGroups = {
        ...prevGroups,
        [groupId]: updatedGroup,
      };

      // Remove grupo se estiver vazio
      if (updatedGroup.length === 0) {
        delete updatedGroups[groupId];
      }

      return updatedGroups;
    });
  };

  const handleChangeCondition = (event, groupId, itemIndex) => {
    const newCondition = event.target.value;

    // Atualiza a condição no estado
    setConditions((prevState) => ({
      ...prevState,
      [itemIndex]: newCondition,
    }));

    // Se a condição for "OU", divide o grupo
    if (newCondition === 'OU') {
      setGroups((prevGroups) => {
        const groupToSplit = [...prevGroups[groupId]];

        // Divide o grupo em dois:
        const firstPart = groupToSplit.slice(0, itemIndex);
        const secondPart = groupToSplit.slice(itemIndex);

        // Cria um novo ID para o segundo grupo
        const newGroupId = `grupo${Object.keys(prevGroups).length + 1}`;

        // Reseta a condição do primeiro item do novo grupo para "E"
        setConditions((prevConditions) => {
          const newConditions = { ...prevConditions };
          // Encontra o índice original que foi movido para o novo grupo
          const movedIndex = itemIndex;
          if (newConditions[movedIndex] === 'OU') {
            delete newConditions[movedIndex]; // Remove a condição OU
          }
          return newConditions;
        });

        return {
          ...prevGroups,
          [groupId]: firstPart,
          [newGroupId]: secondPart,
        };
      });
    }
  };

  const handleChangeFieldValue = (event, groupId, itemIndex) => {
    const newValue = event.target.value;

    setGroups((prevGroups) => {
      const updatedGroup = [...prevGroups[groupId]];
      updatedGroup[itemIndex] = {
        ...updatedGroup[itemIndex],
        value: newValue ?? '',
      };

      return {
        ...prevGroups,
        [groupId]: updatedGroup,
      };
    });

    // Limpa o erro quando o campo é alterado
    setInvalidFields((prev) => {
      const newErrors = { ...prev };
      if (newErrors[groupId]?.[itemIndex]) {
        delete newErrors[groupId][itemIndex];
        if (Object.keys(newErrors[groupId]).length === 0) {
          delete newErrors[groupId];
        }
      }
      return newErrors;
    });
  };

  return (
    <Box py={5}>
      <Typography variant="body1" fontWeight="bold" mb={2}>
        Filtros (StorePlacesQuery):
      </Typography>
      <Autocomplete
        disablePortal
        id="field-selector"
        options={getOptions(form.action)}
        getOptionLabel={(option) => option.fieldName}
        getOptionKey={(option) => option.value}
        onChange={handleSelectFields}
        filterOptions={(options, { inputValue }) => {
          if (!inputValue) return options;
          return options.filter((option) =>
            option.fieldName.toLowerCase().includes(inputValue.toLowerCase())
          );
        }}
        sx={{ width: 300 }}
        value={null}
        noOptionsText="Nenhum campo encontrado"
        clearOnBlur
        blurOnSelect
        renderInput={(params) => (
          <TextField {...params} label="Selecione um campo" variant="outlined" />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.value}
            style={{
              opacity: option.disabled ? 0.5 : 1,
              pointerEvents: option.disabled ? 'none' : 'auto',
            }}
          >
            {option.fieldName}
          </li>
        )}
      />

      {/*  Drag and drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        {Object.keys(groups).map((groupId) => (
          <Droppable key={groupId} droppableId={groupId}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {groups[groupId].map((item, index) => (
                  <Draggable
                    key={`${groupId}-${index}`}
                    draggableId={`${groupId}-${index}`}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <Paper
                          sx={{
                            padding: '2rem',
                            marginBottom: '10px',
                            marginLeft: index > 0 && conditions[index] !== 'OU' ? '40px' : '15px',
                            marginTop: index === 0 && '40px',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: '10px',
                            alignItems: 'center',
                            justifyContent: isMobile ? 'center' : 'space-between',
                            position: 'relative',
                            width:
                              isMdDown && index === 0
                                ? '95%'
                                : isMdDown && index > 0
                                ? '88%'
                                : isMobile && index > 0
                                ? '83%'
                                : index > 0 && conditions[index] !== 'OU'
                                ? '71%'
                                : '77%',
                          }}
                          elevation={1}
                        >
                          {index > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '-30px',
                                backgroundColor: '#fff',
                              }}
                            >
                              <FormControl sx={{ width: '80px' }}>
                                <Select
                                  value={index > 0 ? conditions[index] || conditionsList[0] : 'E'}
                                  onChange={(event) => handleChangeCondition(event, groupId, index)}
                                  size="small"
                                >
                                  {conditionsList.map((condition, i) => (
                                    <MenuItem key={i} value={condition}>
                                      {condition}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}

                          <Box
                            sx={{
                              width: isMobile
                                ? 'max-content'
                                : index > 0 && conditions[index] !== 'OU'
                                ? '170px'
                                : '200px',
                            }}
                          >
                            {getFieldTranslation(item.field)}
                          </Box>

                          <FormControl
                            sx={{
                              width: isMobile ? '250px' : '175px',
                              minWidth: '175px',
                            }}
                          >
                            <Select
                              value={item.type || actions[0]}
                              onChange={(event) =>
                                handleChangeActions(event, groupId, index, item.value)
                              }
                              size="small"
                              MenuProps={{
                                MenuListProps: {
                                  autoFocus: true,
                                  style: { maxHeight: '200px' },
                                },
                              }}
                            >
                              {getFilteredActions(item.field).map((condition, i) => (
                                <MenuItem key={i} value={condition}>
                                  {actionTranslations[condition]}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <Box
                            sx={{
                              position: 'relative',
                              width: isMdUp
                                ? index > 0
                                  ? '370px'
                                  : '400px'
                                : index > 0
                                ? '170px'
                                : '300px',
                              ...(isMobile && { width: '250px' }),
                            }}
                          >
                            {['establishmentId', 'id'].includes(item.field) ? (
                              <>
                                <AutocompleteListType
                                  type={item.type}
                                  typeCard={form.action}
                                  loading={loadingList}
                                  setLoading={setLoadingList}
                                  selectedEstablishment={selectedEstablishment}
                                  setSelectedEstablishment={setSelectedEstablishment}
                                  selectedStore={selectedStore}
                                  setSelectedStore={setSelectedStore}
                                  selectedProduct={selectedProduct}
                                  setSelectedProduct={setSelectedProduct}
                                  selectedIdEdit={selectedIdEdit}
                                  groupId={groupId}
                                  index={index}
                                  setGroups={setGroups}
                                  setInvalidFields={setInvalidFields}
                                  isMobile={isMobile}
                                  error={invalidFields[groupId]?.[index]}
                                />
                                {invalidFields[groupId]?.[index] && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      position: 'absolute',
                                      left: '4px',
                                      top: '100%',
                                    }}
                                  >
                                    Preencha este campo *
                                  </Typography>
                                )}
                              </>
                            ) : ['array_contains', 'array_not_contains'].includes(item.type) ? (
                              <>
                                <ChipArrayContains
                                  setGroups={setGroups}
                                  values={Array.isArray(item.value) ? item.value : []}
                                  groupId={groupId}
                                  itemIndex={index}
                                  setInvalidFields={setInvalidFields}
                                  error={invalidFields[groupId]?.[index]}
                                />
                                {invalidFields[groupId]?.[index] && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      position: 'absolute',
                                      left: '4px',
                                      top: '100%',
                                    }}
                                  >
                                    Preencha este campo *
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <>
                                <TextField
                                  sx={{
                                    width: '100%',
                                    background: '#fff',
                                  }}
                                  error={invalidFields[groupId]?.[index]}
                                  variant="outlined"
                                  size="small"
                                  value={item.value || ''}
                                  onChange={(event) =>
                                    handleChangeFieldValue(event, groupId, index)
                                  }
                                  InputLabelProps={{ shrink: false }}
                                />
                                {invalidFields[groupId]?.[index] && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      position: 'absolute',
                                      left: '4px',
                                      top: '100%',
                                    }}
                                  >
                                    Preencha este campo *
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                          {isMobile && invalidFields[groupId]?.[index] && (
                            <div style={{ padding: '5px' }}></div>
                          )}
                          <Tooltip title="Apagar" placement="top">
                            <InvisbleButton onClick={(e) => handleDeleteDrag(e, groupId, index)}>
                              <DeleteIcon color="error" size={25} />
                            </InvisbleButton>
                          </Tooltip>
                        </Paper>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        ))}
      </DragDropContext>
    </Box>
  );
}
